/**
 * components/Settings/AI/AIKeyManagement.tsx
 * Key management container for AI provider configuration
 *
 * Wraps AISettingsCard and provides action handler implementations:
 * update key (inline edit + validate), re-validate stored key,
 * remove configuration (confirmation modal), and switch provider.
 *
 * Work Unit: WU02 - AI Settings Management
 * Task: T02 - Key Management Actions
 * Sprint: S02 - AI Setup & Settings UI
 * Project: P09 - AI Integration
 */

// 1. External dependencies
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, Eye, EyeOff } from 'lucide-react';

// 2. Workspace packages
import {
  Button,
  Card,
  CardBody,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  useDisclosure,
  useToastNotification,
} from '@enfomentis/ui';

// 3. Internal absolute imports
import type { AIProviderType } from '@/types/shared/AI';
import { AI_PROVIDER_DISPLAY_MAP } from '@/constants/ai';
import { aiCredentialService } from '@/services/AICredentialService';
import { aiProviderManager } from '@/services/ai';

// 4. Relative imports
import { AISettingsCard } from './AISettingsCard';

// ─── Props ──────────────────────────────────────────────────────────────────

/**
 * Props for the AI Key Management container
 *
 * @see AIKeyManagement
 */
export interface AIKeyManagementProps {
  /** The currently configured provider */
  provider: AIProviderType;

  /** Called after any configuration change (save, delete) so parent can refresh state */
  onConfigChange: () => void;

  /** Called when user wants to switch provider (parent re-opens wizard) */
  onSwitchProvider: () => void;
}

// ─── Component ──────────────────────────────────────────────────────────────

/**
 * AI Key Management Container
 *
 * Renders `AISettingsCard` and provides implementations for all
 * management actions: update key, re-validate, remove, switch provider.
 *
 * - **Update Key**: Inline edit with masked input, validation before save
 * - **Re-validate**: Decrypts stored key, validates, updates status
 * - **Remove**: Confirmation modal, then deletes credential
 * - **Switch Provider**: Delegates to parent callback
 *
 * @example
 * ```tsx
 * <AIKeyManagement
 *   provider="openai"
 *   onConfigChange={() => refreshState()}
 *   onSwitchProvider={() => setMode('wizard')}
 * />
 * ```
 */
export const AIKeyManagement: React.FC<AIKeyManagementProps> = ({
  provider,
  onConfigChange,
  onSwitchProvider,
}) => {
  // ─── State ────────────────────────────────────────────────────────────

  const [isEditing, setIsEditing] = useState(false);
  const [newKey, setNewKey] = useState('');
  const [isKeyVisible, setIsKeyVisible] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState('');

  const [isRevalidating, setIsRevalidating] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  // ─── Refs & Hooks ─────────────────────────────────────────────────────

  const mountedRef = useRef(true);
  const removeModal = useDisclosure();
  const { showToast } = useToastNotification();

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // ─── Refresh Key ──────────────────────────────────────────────────────
  // Force AISettingsCard to reload by toggling a key
  const [refreshKey, setRefreshKey] = useState(0);

  const triggerRefresh = useCallback((): void => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  // ─── Update Key Handlers ──────────────────────────────────────────────

  const handleStartEdit = useCallback((): void => {
    setIsEditing(true);
    setNewKey('');
    setIsKeyVisible(false);
    setValidationError('');
  }, []);

  const handleCancelEdit = useCallback((): void => {
    setIsEditing(false);
    setNewKey('');
    setIsKeyVisible(false);
    setValidationError('');
  }, []);

  const handleSaveKey = useCallback(async (): Promise<void> => {
    if (newKey.trim().length === 0) return;

    setIsValidating(true);
    setValidationError('');

    try {
      const result = await aiProviderManager.validateKey(provider, newKey);

      if (!mountedRef.current) return;

      if (result.valid) {
        await aiCredentialService.saveCredential(provider, newKey, 'valid');
        if (!mountedRef.current) return;

        setIsEditing(false);
        setNewKey('');
        triggerRefresh();
        onConfigChange();
        showToast({
          title: 'API key updated',
          description: 'Your new key has been validated and saved.',
          color: 'success',
        });
      } else {
        setValidationError(
          result.error ?? 'Invalid API key. Please check and try again.',
        );
      }
    } catch {
      if (!mountedRef.current) return;
      setValidationError('Could not validate key. Check your connection and try again.');
    } finally {
      if (mountedRef.current) {
        setIsValidating(false);
      }
    }
  }, [provider, newKey, onConfigChange, showToast, triggerRefresh]);

  // ─── Re-validate Handler ──────────────────────────────────────────────

  const handleRevalidate = useCallback(async (): Promise<void> => {
    setIsRevalidating(true);

    try {
      const decryptedKey = await aiCredentialService.getDecryptedKey(provider);

      if (!mountedRef.current) return;

      if (!decryptedKey) {
        showToast({
          title: 'Re-validation failed',
          description: 'Could not decrypt stored key. Try updating your key instead.',
          color: 'danger',
        });
        return;
      }

      const result = await aiProviderManager.validateKey(provider, decryptedKey);

      if (!mountedRef.current) return;

      const newStatus = result.valid ? 'valid' as const : 'invalid' as const;
      await aiCredentialService.updateValidationStatus(provider, newStatus);

      if (!mountedRef.current) return;

      triggerRefresh();

      if (result.valid) {
        showToast({
          title: 'Key is valid',
          description: 'Your API key has been re-validated successfully.',
          color: 'success',
        });
      } else {
        showToast({
          title: 'Key validation failed',
          description: result.error ?? 'Your API key is no longer valid.',
          color: 'danger',
        });
      }
    } catch {
      if (!mountedRef.current) return;
      showToast({
        title: 'Re-validation failed',
        description: 'Could not connect. Check your internet connection.',
        color: 'danger',
      });
    } finally {
      if (mountedRef.current) {
        setIsRevalidating(false);
      }
    }
  }, [provider, showToast, triggerRefresh]);

  // ─── Remove Handler ───────────────────────────────────────────────────

  const handleConfirmRemove = useCallback(async (): Promise<void> => {
    setIsRemoving(true);

    try {
      await aiCredentialService.deleteCredential(provider);

      if (!mountedRef.current) return;

      removeModal.onOpenChange();
      onConfigChange();
      showToast({
        title: 'AI configuration removed',
        description: 'You can set up AI again at any time.',
        color: 'success',
      });
    } catch {
      if (!mountedRef.current) return;
      showToast({
        title: 'Failed to remove',
        description: 'Could not remove AI configuration. Please try again.',
        color: 'danger',
      });
    } finally {
      if (mountedRef.current) {
        setIsRemoving(false);
      }
    }
  }, [provider, onConfigChange, removeModal, showToast]);

  // ─── Render ───────────────────────────────────────────────────────────

  const providerName = AI_PROVIDER_DISPLAY_MAP[provider];

  return (
    <div className="space-y-4">
      {/* Settings Card */}
      <AISettingsCard
        key={refreshKey}
        onUpdateKey={handleStartEdit}
        onRevalidate={handleRevalidate}
        onSwitchProvider={onSwitchProvider}
        onRemove={removeModal.onOpen}
      />

      {/* Re-validate loading indicator (overlay on card action) */}
      {isRevalidating && (
        <Card className="w-full">
          <CardBody className="flex items-center justify-center p-4">
            <p className="text-sm text-default-500">Re-validating your API key...</p>
          </CardBody>
        </Card>
      )}

      {/* Inline Key Edit */}
      {isEditing && (
        <Card className="w-full">
          <CardBody className="space-y-4 p-4 sm:p-6">
            <h4 className="text-base font-semibold">Update API Key</h4>

            <Input
              label={`New ${providerName} API Key`}
              placeholder="Enter your new API key"
              type={isKeyVisible ? 'text' : 'password'}
              value={newKey}
              onValueChange={setNewKey}
              endContent={
                <button
                  type="button"
                  className="text-default-400 hover:text-default-600"
                  onClick={() => setIsKeyVisible((prev) => !prev)}
                  aria-label={isKeyVisible ? 'Hide API key' : 'Show API key'}
                >
                  {isKeyVisible ? (
                    <EyeOff className="size-4" />
                  ) : (
                    <Eye className="size-4" />
                  )}
                </button>
              }
              isInvalid={validationError.length > 0}
              errorMessage={validationError}
              autoFocus
            />

            {validationError && (
              <div className="flex items-center gap-2">
                <AlertTriangle className="size-4 shrink-0 text-danger" />
                <span className="text-sm text-danger">{validationError}</span>
              </div>
            )}

            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button
                variant="light"
                onPress={handleCancelEdit}
                className="w-full sm:w-auto"
              >
                Cancel
              </Button>
              <Button
                color="primary"
                isDisabled={newKey.trim().length === 0}
                isLoading={isValidating}
                onPress={handleSaveKey}
                className="w-full sm:w-auto"
              >
                Validate & Save
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Remove Confirmation Modal */}
      <Modal isOpen={removeModal.isOpen} onOpenChange={removeModal.onOpenChange}>
        <ModalContent>
          <ModalHeader>Remove AI Configuration</ModalHeader>
          <ModalBody>
            <p className="text-sm text-default-600">
              Are you sure you want to remove your {providerName} configuration?
              You&apos;ll need to re-enter your API key to use AI features.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="light"
              onPress={removeModal.onOpenChange}
            >
              Cancel
            </Button>
            <Button
              color="danger"
              isLoading={isRemoving}
              onPress={handleConfirmRemove}
            >
              Remove
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};
